import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  const addToCart = useCallback((service) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(item => item.id === service.id);
      
      if (existingItem) {
        // If item already in cart, increase quantity
        return prevItems.map(item =>
          item.id === service.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      
      // Add new item to cart
      return [...prevItems, { ...service, quantity: 1 }];
    });
    
    // Set as selected service for booking
    setSelectedService(service);
  }, []);

  const removeFromCart = useCallback((serviceId) => {
    setCartItems((prevItems) => prevItems.filter(item => item.id !== serviceId));
  }, []);

  const updateQuantity = useCallback((serviceId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(serviceId);
      return;
    }
    
    setCartItems((prevItems) =>
      prevItems.map(item =>
        item.id === serviceId ? { ...item, quantity } : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setSelectedService(null);
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cartItems]);

  const getCartCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  const value = {
    cartItems,
    selectedService,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
